import { Fragment, useCallback, useEffect, useState } from 'react'

import { formatDate } from '../../lib/datetime'
import { errorText } from '../../lib/offsite'
import { PeriodFilter } from '../../lib/PeriodFilter'
import type { PeriodValue } from '../../lib/PeriodFilter'
import { useSession } from '../../lib/SessionContext'
import { SectionHead } from '../offsite/parts'

/* ============================================================================
   สรุปการทำงานรายคน — แทน frmReportAll ของ HR_Tech
   API: backend/src/routes/adminWorkSummary.js · lib/workSummary.js

   ── ทำไมหน้าเดียวตอบได้ 5 รายงาน ───────────────────────────────────────────
   ของเดิมเป็นหน้าเดียวที่มี 5 แท็บ (ทั้งหมด / เบี้ยขยัน / ขาด-สาย / การลา / โอที)
   ทุกแท็บใช้ **ชุดข้อมูลเดียวกัน** ต่างกันแค่กรองแถวกับเลือกคอลัมน์มาโชว์
   ที่นี่จึงยิง API เส้นเดียวแล้วสลับมุมมองในหน้า ไม่ต้องโหลดใหม่ทุกครั้งที่เปลี่ยนแท็บ

   ── ตัวเลขตรงกับของเดิมไหม ─────────────────────────────────────────────────
   ตรวจแล้วกับ ก.ย. 2026: 116 แถว ตรงกันทุกคอลัมน์ รวมผลเบี้ยขยัน
   (เทียบกับ query เดิมของ frmReportAll แถวต่อแถว)
   ============================================================================ */

type Row = {
  empCode: string
  empName: string
  companyCode: string
  buDesc: string | null
  branchCode: string | null
  branchName: string | null
  approverName: string | null
  workStartDate: string | null
  totalDays: number
  workDays: number
  holidays: number
  lateDays: number
  absentDays: number
  leaveDays: number
  adjustDays: number
  badDays: number
  diligent: boolean
}

type Result = {
  range: { from: string; to: string; days: number }
  items: Row[]
  totals: {
    people: number
    diligent: number
    lateDays: number
    absentDays: number
    leaveDays: number
    adjustDays: number
  }
}

type View = 'all' | 'diligent' | 'problem' | 'leave'

/** รายวันของคนเดียวในช่วงเดียวกับที่สรุป — /api/admin/person/:emp/range */
type DetailDay = {
  workDate: string
  dow: number
  isHoliday: boolean
  noTimeRequired: boolean
  shiftStart: string | null
  shiftEnd: string | null
  punchIn: string | null
  punchOut: string | null
  inTime: string | null
  outTime: string | null
  correctedIn: boolean
  correctedOut: boolean
  outDayOffset: number
  lateMinute: number
  absent: boolean
  onLeave: boolean
  leaveTypeDesc: string | null
  leaves: { leaveTypeDesc: string; from: string | null; to: string | null; approved: boolean; statusText: string }[]
  timeEdits: { leaveTypeDesc: string; from: string | null; to: string | null; toNextDay?: boolean; approved: boolean; statusText: string }[]
  workDayRemark: string | null
}

type Detail = {
  employee: { empCode: string; fullName: string | null }
  range: { from: string; to: string }
  work: { days: DetailDay[] }
  ot: { items: { otDate: string; minutes: number; rate: number | null }[] }
}

const BASE = '/api/admin/work-summary'
const nf = new Intl.NumberFormat('th-TH')

const VIEWS: { key: View; label: string; note: string }[] = [
  { key: 'all', label: 'ทั้งหมด', note: 'ทุกคนในช่วงที่เลือก' },
  { key: 'diligent', label: 'เบี้ยขยัน', note: 'เฉพาะคนที่ไม่มีข้อบกพร่องเลยทั้งช่วง' },
  { key: 'problem', label: 'ขาด / สาย', note: 'เฉพาะคนที่มีวันขาดหรือมาสาย' },
  { key: 'leave', label: 'การลา', note: 'เฉพาะคนที่มีวันลาหรือใบปรับปรุงเวลา' },
]

export default function WorkSummary() {
  const { call } = useSession()

  /* ค่าเริ่มต้น = รอบเงินเดือนที่ครอบวันนี้ (21 ถึง 20) — ตัวเลขพวกนี้เอาไปใช้
     คู่กับรอบจ่ายเงิน การตั้งต้นเป็นเดือนปฏิทินทำให้คนต้องมานั่งปรับวันทุกครั้ง
     ช่วงจริงมาจาก PeriodFilter ซึ่งอ่านรอบจาก HR_OT_PERIOD ไม่ได้คำนวณเอง */
  const [period, setPeriod] = useState<PeriodValue | null>(null)
  const from = period?.from ?? ''
  const to = period?.to ?? ''
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')
  const [view, setView] = useState<View>('all')

  const [data, setData] = useState<Result | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* กดดูรายละเอียดรายคน — เปิดใต้แถวที่กด ไม่เด้งออกไปหน้าอื่น
     ใช้ /person/:emp/range เพื่อให้ได้ช่วงเดียวกับที่สรุปไว้เป๊ะ ๆ
     (รอบเงินเดือนคร่อม 2 เดือน หน้า /person/:emp/month เดิมทำไม่ได้) */
  const [openEmp, setOpenEmp] = useState<string | null>(null)
  const [detail, setDetail] = useState<Detail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const toggleDetail = async (empCode: string) => {
    if (openEmp === empCode) {
      setOpenEmp(null)
      return
    }
    setOpenEmp(empCode)
    setDetail(null)
    setDetailError(null)
    setDetailLoading(true)
    try {
      const qs = new URLSearchParams({ from, to })
      setDetail(
        await call<Detail>(`/api/admin/person/${encodeURIComponent(empCode)}/range?${qs}`),
      )
    } catch (err) {
      setDetailError(errorText(err))
    } finally {
      setDetailLoading(false)
    }
  }

  const load = useCallback(async () => {
    // ยังไม่รู้ช่วง (รอ PeriodFilter โหลดรอบเสร็จ) — ยิงไปก็โดน 400 เปล่า ๆ
    if (!from || !to) return
    setLoading(true)
    try {
      const qs = new URLSearchParams({ from, to })
      if (q) qs.set('q', q)
      setData(await call<Result>(`${BASE}?${qs}`))
      setError(null)
    } catch (err) {
      setError(errorText(err))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [call, from, to, q])

  useEffect(() => {
    void load()
  }, [load])

  const all = data?.items ?? []
  const rows = all.filter((r) => {
    if (view === 'diligent') return r.diligent
    if (view === 'problem') return r.absentDays > 0 || r.lateDays > 0
    if (view === 'leave') return r.leaveDays > 0 || r.adjustDays > 0
    return true
  })

  /** ดาวน์โหลดเป็น CSV — ของเดิมพิมพ์เป็นรายงาน ที่นี่ให้เอาไปทำต่อใน Excel */
  const exportCsv = () => {
    const head = [
      'รหัส', 'ชื่อ', 'บริษัท', 'BU', 'สาขา', 'ผู้อนุมัติ', 'วันเริ่มงาน',
      'วันทั้งหมด', 'วันทำงาน', 'วันหยุด', 'สาย', 'ขาด', 'ลา', 'ปรับเวลา', 'เบี้ยขยัน',
    ]
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const body = rows.map((r) =>
      [
        r.empCode, r.empName, r.companyCode, r.buDesc ?? '', r.branchName ?? '',
        r.approverName ?? '', r.workStartDate ?? '',
        r.totalDays, r.workDays, r.holidays, r.lateDays, r.absentDays,
        r.leaveDays, r.adjustDays, r.diligent ? 'ได้' : '',
      ]
        .map(esc)
        .join(','),
    )
    // BOM ให้ Excel อ่านภาษาไทยออก — ไม่ใส่แล้วเปิดมาเป็นตัวต่างด้าว
    const blob = new Blob(['﻿' + [head.map(esc).join(','), ...body].join('\r\n')], {
      type: 'text/csv;charset=utf-8',
    })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `สรุปการทำงาน_${from}_ถึง_${to}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <>
      <h1>สรุปการทำงาน</h1>
      <p className="meta">
        ขาดงาน มาสาย วันลา และใบปรับปรุงเวลา รายคนในช่วงที่เลือก พร้อมผลเบี้ยขยัน —
        ชุดข้อมูลเดียวกับรายงานของโปรแกรมเดิม ตรวจแล้วว่าตัวเลขตรงกัน
      </p>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}

      <section className="card">
        <SectionHead
          title="สรุปรายคน"
          meta={data ? `${nf.format(rows.length)} คน` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setQ(term.trim())
          }}
        >
          {/* เปลี่ยนช่วงแล้วต้องปิดรายละเอียดที่เปิดค้างไว้ — ไม่งั้นข้างบนเป็นรอบใหม่
              แต่ตารางที่กางอยู่ยังเป็นรอบเก่า */}
          <PeriodFilter
            value={period}
            onChange={(v) => {
              setOpenEmp(null)
              setPeriod(v)
            }}
            disabled={loading}
          />
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน หรือชื่อ"
            />
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
          {rows.length > 0 && (
            <button type="button" className="ghost" onClick={exportCsv}>
              ดาวน์โหลด CSV
            </button>
          )}
        </form>

        {/* ตัวเลขสรุปคิดจากทั้งช่วง ไม่ใช่เฉพาะมุมมองที่เลือก */}
        {data && (
          <p className="meta">
            {period?.mode === 'round' && period.roundId && (
              <>
                รอบ <strong>{period.roundId}</strong> ·{' '}
              </>
            )}
            {data.range.from} ถึง {data.range.to} ({data.range.days} วัน) ·{' '}
            <strong>{nf.format(data.totals.people)}</strong> คน · ได้เบี้ยขยัน{' '}
            <strong>{nf.format(data.totals.diligent)}</strong> คน · รวมวันขาด{' '}
            {nf.format(data.totals.absentDays)} · วันสาย {nf.format(data.totals.lateDays)} ·
            วันลา {nf.format(data.totals.leaveDays)} · ใบปรับเวลา{' '}
            {nf.format(data.totals.adjustDays)}
          </p>
        )}

        {/* สลับมุมมองโดยไม่ยิง API ใหม่ — ข้อมูลชุดเดียวกัน */}
        <div className="toolbar">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              className={view === v.key ? undefined : 'ghost'}
              title={v.note}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>

        {data === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : rows.length === 0 ? (
          <p className="meta">
            {all.length === 0
              ? 'ไม่มีข้อมูลในช่วงวันที่เลือก'
              : `ไม่มีใครเข้าเงื่อนไข "${VIEWS.find((v) => v.key === view)?.label}" ในช่วงนี้`}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th>ชื่อ</th>
                  <th>BU / สาขา</th>
                  <th>วันทำงาน</th>
                  <th>วันหยุด</th>
                  <th>สาย</th>
                  <th>ขาด</th>
                  <th>ลา</th>
                  <th>ปรับเวลา</th>
                  <th>เบี้ยขยัน</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={`${r.companyCode}-${r.empCode}`}>
                    <tr>
                      <td className="nowrap">{r.empCode}</td>
                      <td>{r.empName}</td>
                      <td>
                        <span className="meta">
                          {r.buDesc ?? '-'}
                          {r.branchName ? ` · ${r.branchName}` : ''}
                        </span>
                      </td>
                      <td>{r.workDays}</td>
                      <td className="meta">{r.holidays}</td>
                      <td>{r.lateDays > 0 ? <strong className="bad">{r.lateDays}</strong> : '-'}</td>
                      <td>
                        {r.absentDays > 0 ? <strong className="bad">{r.absentDays}</strong> : '-'}
                      </td>
                      <td>{r.leaveDays || '-'}</td>
                      <td>{r.adjustDays || '-'}</td>
                      <td className="nowrap">
                        {r.diligent ? (
                          <span className="ok">ได้</span>
                        ) : (
                          <span className="meta">เสีย {r.badDays} วัน</span>
                        )}
                      </td>
                      <td className="nowrap">
                        <button
                          type="button"
                          className="ghost tiny"
                          aria-expanded={openEmp === r.empCode}
                          onClick={() => void toggleDetail(r.empCode)}
                        >
                          {openEmp === r.empCode ? 'ปิด' : 'ดูรายวัน'}
                        </button>
                      </td>
                    </tr>

                    {/* กางใต้แถวที่กด — เห็นทั้งเวลาเข้าออก วันลา และใบปรับเวลา
                        ในช่วงเดียวกับที่สรุป ไม่ต้องเปิดหน้าอื่น */}
                    {openEmp === r.empCode && (
                      <tr>
                        <td colSpan={11} className="subrow">
                          {detailLoading && <p className="meta">กำลังโหลด…</p>}
                          {detailError && <p className="error">{detailError}</p>}
                          {detail && <DayTable detail={detail} />}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="meta">
          <strong>เบี้ยขยัน</strong> ได้ต่อเมื่อไม่มีข้อบกพร่องเลยสักวันในช่วงที่เลือก —
          วันที่นับว่าเสียคือ ขาดงาน มาสาย ออกก่อนเวลา ไม่มีเวลาออก
          หรือลาประเภทอื่นที่ไม่ใช่ลาพักร้อนเต็มวัน · วันหยุดและใบปรับปรุงเวลาไม่เสียสิทธิ
        </p>
      </section>
    </>
  )
}

/**
 * ตารางรายวันที่กางใต้แถว — เห็นเวลาเข้าออก วันลา และใบปรับปรุงเวลาในที่เดียว
 *
 * ⚠️ คอลัมน์ "ตอก" คือเวลาที่เครื่องบันทึกจริง (punchIn/punchOut)
 *    ส่วน "นับเป็น" คือเวลาหลังใบปรับปรุงเวลา ซึ่งเป็นตัวที่เอาไปคิดสาย/ขาด
 *    ต้องเห็นทั้งสองฝั่ง ไม่งั้นดูไม่ออกว่าใบปรับเวลาเปลี่ยนอะไรไปบ้าง
 */
function DayTable({ detail }: { detail: Detail }) {
  const otByDate = new Map<string, number>()
  for (const o of detail.ot.items) {
    otByDate.set(o.otDate, (otByDate.get(o.otDate) ?? 0) + o.minutes)
  }

  // วันที่ไม่มีอะไรน่าสนใจเลยไม่ต้องรก — เหลือเฉพาะวันทำงานกับวันที่มีเรื่อง
  const days = detail.work.days.filter(
    (d) =>
      !d.isHoliday ||
      d.onLeave ||
      d.timeEdits.length > 0 ||
      d.punchIn ||
      d.punchOut ||
      otByDate.has(d.workDate),
  )

  if (days.length === 0) return <p className="meta">ไม่มีวันทำงานในช่วงนี้</p>

  return (
    <div className="table-wrap table-wrap--rows10">
      <table className="data">
        <thead>
          <tr>
            <th className="nowrap">วันที่</th>
            <th className="nowrap">กะ</th>
            <th className="nowrap">ตอกเข้า</th>
            <th className="nowrap">ตอกออก</th>
            <th className="num">สาย</th>
            <th className="num">OT</th>
            <th>สถานะ / ใบที่เกี่ยวข้อง</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const ot = otByDate.get(d.workDate) ?? 0
            return (
              <tr key={d.workDate} className={d.isHoliday ? 'off' : undefined}>
                <td className="nowrap">{formatDate(d.workDate)}</td>
                <td className="nowrap meta">
                  {d.isHoliday
                    ? 'วันหยุด'
                    : d.shiftStart && d.shiftEnd
                      ? `${d.shiftStart}-${d.shiftEnd}`
                      : '—'}
                </td>
                <td className="nowrap">
                  {d.punchIn ?? '—'}
                  {d.correctedIn && <span className="cell-note">นับ {d.inTime ?? '—'}</span>}
                </td>
                <td className="nowrap">
                  {d.punchOut ?? '—'}
                  {d.outDayOffset > 0 && <span className="chip">วันถัดไป</span>}
                  {d.correctedOut && <span className="cell-note">นับ {d.outTime ?? '—'}</span>}
                </td>
                <td className="num">{d.lateMinute || '—'}</td>
                <td className="num">{ot ? Math.round((ot / 60) * 100) / 100 : '—'}</td>
                <td>
                  {d.onLeave && (
                    <span className="chip chip--warn">{d.leaveTypeDesc ?? 'ลา'}</span>
                  )}
                  {d.absent && <span className="chip chip--bad">ขาดงาน</span>}
                  {d.leaves.map((lv, i) => (
                    <div key={`l${i}`} className="cell-note">
                      {lv.leaveTypeDesc}
                      {lv.from || lv.to ? ` ${lv.from ?? ''}–${lv.to ?? ''}` : ''}
                      {!lv.approved ? ` · ${lv.statusText}` : ''}
                    </div>
                  ))}
                  {/* ใบปรับปรุงเวลา — ของจริง 1,061 ใบใน 12 เดือนกรอกด้านเดียว
                      จึงมีปลายเดียว ต้องโชว์เท่าที่มี */}
                  {d.timeEdits.map((te, i) => (
                    <div key={`t${i}`} className="cell-note">
                      ปรับเวลา{te.from || te.to ? ` ${te.from ?? ''}–${te.to ?? ''}` : ''}
                      {te.toNextDay ? ' (วันถัดไป)' : ''}
                      {!te.approved ? ` · ${te.statusText}` : ''}
                    </div>
                  ))}
                  {!d.onLeave && !d.absent && d.timeEdits.length === 0 && (
                    <span className="meta">{d.workDayRemark || '—'}</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
